// Copyright 2020 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";

@autoinject
export class MeasurementSearch implements IModal<MeasurementSearchModalState, unknown> {
    private state: MeasurementSearchModalState;

    protected searchFilters: SearchFilters;
    protected selectableDistanceRange: { from: number; to: number };
    protected selectableMeasurementTimeRange: { from: number; to: number };

    constructor() {}

    public activateModal(
        state: MeasurementSearchModalState,
        modalInstance: IWorkflowModuleModalInstance<MeasurementSearchModalState, unknown>
    ): void | Promise<void> {
        this.state = state;

        this.initSearchProperties();
    }

    public updateState(newState: MeasurementSearchModalState): void {
        this.state = newState;

        this.initSearchProperties(newState.filters);

        this.filter();
    }

    public initSearchProperties(searchFilters?: SearchFilters): void {
        if (this.state == undefined || this.state.measurements == undefined) {
            return;
        }

        const filters = searchFilters == undefined ? new SearchFilters() : searchFilters;

        const metadatas: { key: string; values: unknown[]; filter: unknown }[] = [];

        for (const measurement of this.state.measurements) {
            if (measurement.metadata == undefined) {
                continue;
            }

            for (const [key, value] of Object.entries(measurement.metadata)) {
                const entry = metadatas.find((x) => x.key == key);

                if (entry == undefined) {
                    const newEntry = {
                        key: key,
                        values: [value],
                        filter: undefined,
                    };
                    metadatas.push(newEntry);
                } else {
                    entry.values.push(value);
                }
            }
        }

        filters.metadataFilters = metadatas;

        if (filters.measurementTimeRange == undefined) {
            // Init other properties
            filters.measurementTimeRange = {
                from: Math.min(...this.state.measurements.map((x) => x.duration)),
                to: Math.max(...this.state.measurements.map((x) => x.duration)),
            };
        }

        this.selectableMeasurementTimeRange = {
            from: Math.min(...this.state.measurements.map((x) => x.duration)),
            to: Math.max(...this.state.measurements.map((x) => x.duration)),
        };

        if (filters.distanceRange == undefined) {
            filters.distanceRange = {
                from: Math.min(...this.state.measurements.map((x) => x.distance)),
                to: Math.max(...this.state.measurements.map((x) => x.distance)),
            };
        }

        this.selectableDistanceRange = {
            from: Math.min(...this.state.measurements.map((x) => x.distance)),
            to: Math.max(...this.state.measurements.map((x) => x.distance)),
        };

        if (filters.measurementDateRange == undefined) {
            filters.measurementDateRange = {
                from: new Date(Math.min(...this.state.measurements.map((x) => new Date(x.created).getTime()))),
                to: new Date(Math.max(...this.state.measurements.map((x) => new Date(x.created).getTime()))),
            };
        }

        this.searchFilters = filters;
    }

    public filter(): void {
        if (this.state == undefined || this.state.measurements == undefined) {
            return;
        }

        const filter = new SearchFilters(this.searchFilters);

        this.state.filteredMeasurementCallback(filter);
    }
}

export class SearchFilters {
    constructor(filter?: SearchFilters) {
        if (filter) {
            this.nameFilter = filter.nameFilter;
            this.noteFilter = filter.noteFilter;
            this.distanceRange = filter.distanceRange
                ? { from: filter.distanceRange.from, to: filter.distanceRange.to }
                : undefined;
            this.measurementTimeRange = filter.measurementTimeRange
                ? { from: filter.measurementTimeRange.from, to: filter.measurementTimeRange.to }
                : undefined;
            this.measurementDateRange = filter.measurementDateRange
                ? { from: filter.measurementDateRange.from, to: filter.measurementDateRange.to }
                : undefined;

            if (filter.metadataFilters) {
                this.metadataFilters = filter.metadataFilters.map((x) => {
                    const obj = {
                        key: x.key,
                        values: x.values,
                        filter: x.filter,
                    };

                    return obj;
                });
            }
        }
    }

    public nameFilter: string;
    public noteFilter: string;
    public distanceRange: { from: number; to: number };
    public measurementTimeRange: { from: number; to: number };
    public measurementDateRange: { from: Date; to: Date };
    public metadataFilters: { key: string; values: unknown[]; filter: unknown }[] = [];
}

export class MeasurementSearchModalState {
    public measurements: GenericMeasurement[];
    public filters?: SearchFilters;

    public filteredMeasurementCallback: (searchFilters: SearchFilters) => void;
}
